import { LoadingButton } from "@altimateai/lego";
import { IconDownload } from "@tabler/icons-react";
import { FC, useCallback, useState } from "react";
import { toast } from "@altimateai/lego";
import { useChatContext } from "../../ChatProvider";

interface DownloadMessageProps {
  sessionId?: string;
}

export const DownloadMessage: FC<DownloadMessageProps> = ({ sessionId }) => {
  const { urls, requestParams } = useChatContext();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = useCallback(async () => {
    if (!sessionId) {
      console.warn("No session available for PDF export");
      return;
    }

    try {
      setIsDownloading(true);

      const endpoint = `${urls?.origin || ""}/studio/session_to_pdf`;
      const payload = { session_id: sessionId };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...(requestParams?.headers || {}),
          "Content-Type": "application/json",
          Accept: "application/pdf",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();

      const filename = `altimateai-${sessionId}-report.pdf`;

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: "Success",
        description: "PDF downloaded successfully.",
      });
    } catch (e) {
      console.error("Failed to download PDF:", e);
      toast({
        title: "Error",
        description: "Failed to download PDF.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [sessionId, requestParams?.headers, urls?.origin]);

  return (
    <LoadingButton
      variant="outline"
      className={"al-bg-transparent al-text-gray-500"}
      size="sm"
      onClick={handleDownloadPdf}
      disabled={isDownloading || !sessionId}
      loading={isDownloading}
      loadingText={"Downloading..."}
      aria-label="Download as PDF"
    >
      <IconDownload aria-hidden="true" />
      Download
    </LoadingButton>
  );
};
